import { useEffect, useRef, useState } from "react";
import { useHeroVideoDiagnostics, iframeDiagnosticHandlers } from "@/lib/heroDiagnostics";

export type HeroMediaType = "image" | "video";

export interface HeroMediaBackgroundProps {
  /** Diagnostics source label (e.g. "home", "banner", "cars"). */
  source: string;
  mediaType: HeroMediaType;
  /** Image URL (when mediaType=image) or video URL — direct .mp4/.webm or YouTube link. */
  url?: string;
  /** Optional poster image for direct <video>. */
  videoPoster?: string;
  /**
   * Tailwind opacity utility applied to the media element (e.g. "opacity-60").
   * Lets the home hero dim the video while keeping the standard banner full-strength.
   */
  mediaClassName?: string;
}

export function extractYouTubeId(url: string): string | null {
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]+)/,
  );
  return match ? match[1] : null;
}

function buildEmbedUrl(youtubeId: string, autoplayDisabled: boolean): string {
  const params = new URLSearchParams({
    autoplay: autoplayDisabled ? "0" : "1",
    mute: "1",
    loop: "1",
    playlist: youtubeId,
    controls: "0",
    showinfo: "0",
    rel: "0",
    modestbranding: "1",
    playsinline: "1",
    disablekb: "1",
    fs: "0",
    iv_load_policy: "3",
  });
  return `https://www.youtube.com/embed/${youtubeId}?${params.toString()}`;
}

/**
 * Shared hero background. Centralises the YouTube iframe / direct <video> /
 * background-image rendering so every hero on the site behaves identically
 * (autoplay/mute/loop params, referrer policy, diagnostics, lazy preload).
 *
 * Renders nothing for image heroes — the parent <section> applies the image
 * via background-image so SSR-friendly above-the-fold paint stays unchanged.
 */
export function HeroMediaBackground({
  source,
  mediaType,
  url,
  videoPoster,
  mediaClassName = "",
}: HeroMediaBackgroundProps) {
  const hasUrl = !!url && url.trim().length > 0;
  const youtubeId = mediaType === "video" && hasUrl ? extractYouTubeId(url!) : null;
  const isDirectVideo = mediaType === "video" && hasUrl && !youtubeId;

  // Debug toggle: append `?heroAutoplay=0` to disable autoplay so we can verify
  // mute/loop/playsinline params load correctly even when autoplay is blocked.
  const autoplayDisabled =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("heroAutoplay") === "0";

  const embedUrl = youtubeId ? buildEmbedUrl(youtubeId, autoplayDisabled) : null;

  useHeroVideoDiagnostics({
    source,
    mediaType,
    sourceUrl: url ?? "",
    youtubeId,
    embedUrl,
  });

  // Lazy preload for direct <video>: start at "none", upgrade to "metadata"
  // once the element scrolls within 200px of the viewport.
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoPreload, setVideoPreload] = useState<"none" | "metadata">("none");
  useEffect(() => {
    if (!isDirectVideo) return;
    if (typeof window === "undefined") return;
    const el = videoRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVideoPreload("metadata");
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [isDirectVideo]);

  if (youtubeId && embedUrl) {
    // Size the iframe so it always covers the parent at 16:9 — pick whichever
    // dimension makes the video overflow rather than letterbox. min-w-[177.78vh]
    // keeps width ≥ height*16/9; min-h-[56.25vw] keeps height ≥ width*9/16.
    // Result: no black bars on either portrait mobile or landscape desktop.
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <iframe
          src={embedUrl}
          title="Hero video"
          width="1920"
          height="1080"
          className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-full w-full min-h-[56.25vw] min-w-[177.78vh] ${mediaClassName}`}
          style={{ border: 0 }}
          allow="autoplay; encrypted-media"
          referrerPolicy="strict-origin-when-cross-origin"
          loading="lazy"
          {...iframeDiagnosticHandlers(source, embedUrl)}
        />
      </div>
    );
  }

  if (isDirectVideo) {
    return (
      <video
        ref={videoRef}
        src={url}
        poster={videoPoster}
        autoPlay
        muted
        loop
        playsInline
        preload={videoPreload}
        width={1920}
        height={1080}
        className={`absolute inset-0 h-full w-full object-cover ${mediaClassName}`}
      />
    );
  }

  return null;
}
